import React from 'react';
import { motion } from 'framer-motion';
import { Bookmark, TrendingUp } from 'lucide-react';

const BookmarksAnalysisCard = ({ bookmarksAnalysis }) => {
  if (!bookmarksAnalysis || bookmarksAnalysis.totalBookmarks === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No bookmarks data available</p>
      </motion.div>
    );
  }

  const { totalBookmarks, questionBookmarks, resourceBookmarks, mostBookmarkedTopics, bookmarkPattern, bookmarkUsageRate } = bookmarksAnalysis;

  const getPatternColor = (pattern) => {
    switch (pattern) {
      case 'strategic':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'excessive':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'minimal':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-600 rounded-lg">
            <Bookmark className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Bookmarks Analysis</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{totalBookmarks} items bookmarked</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPatternColor(bookmarkPattern)}`}>
          {bookmarkPattern}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Questions</p>
          <p className="text-3xl font-bold text-amber-600">{questionBookmarks}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Resources</p>
          <p className="text-3xl font-bold text-orange-600">{resourceBookmarks}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Usage Rate</p>
          <p className="text-3xl font-bold text-yellow-600">{bookmarkUsageRate}%</p>
        </motion.div>
      </div>

      {/* Most Bookmarked Topics */}
      {mostBookmarkedTopics && mostBookmarkedTopics.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-amber-600" />
            Most Bookmarked Topics
          </h4>
          <div className="flex flex-wrap gap-2">
            {mostBookmarkedTopics.map((topic, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-sm font-medium"
              >
                {topic}
              </motion.span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default BookmarksAnalysisCard;
